import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { FileText, AlertTriangle, Shield, Users, TrendingUp, Calendar, BarChart3, BookOpen, Search, ArrowRight } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'
import { GovernanceCard } from '../../components/governance/GovernanceCard'
import { ProblemCard } from '../../components/ui/ProblemCard'
import overviewStyles from '@/styles/pages/governance/GovernanceOverview.module.css'

const GOVERNANCE_SEVERITY_KEYS = ['Critical', 'High', 'Medium', 'Low'] as const
type GovernanceSeverityKey = (typeof GOVERNANCE_SEVERITY_KEYS)[number]

const SEVERITY_TO_TONE: Record<
  GovernanceSeverityKey,
  'critical' | 'high' | 'medium' | 'low'
> = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
}

interface OverviewData {
  governanceScore?: {
    overallScore: number
    componentScores?: {
      policyLifecycle: number
      auditCoverage: number
      riskMaturity: number
      awarenessReadiness: number
      evidenceCoverage: number
    }
    status: 'Green' | 'Yellow' | 'Red'
  }
  metrics: {
    activePolicies: number
    policiesDueForReview: {
      next30Days: number
      next60Days: number
      next90Days: number
    }
    openAuditFindings: {
      total: number
      bySeverity: {
        Critical: number
        High: number
        Medium: number
        Low: number
      }
    }
    openRisks: {
      total: number
      byLevel: {
        Critical: number
        High: number
        Medium: number
        Low: number
      }
    }
    trainingCompletionRate: string
  }
  charts: {
    policyUpdateFrequency: Array<{ month: string; count: number }>
    openCorrectiveActions: number
    riskHeatmap: any[][]
    evidenceAging: Array<{ days: number; count: number }>
  }
}

const GovernanceOverview: React.FC = () => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const governanceNavigationCards = useMemo(
    () => [
      {
        to: '/governance/policies',
        icon: <FileText className="h-5 w-5 shrink-0 text-blue-600" aria-hidden />,
        title: t('governance.overview.navigationCards.policiesLibrary', 'Policies Library'),
        description: t(
          'governance.overview.navigationCards.policiesLibraryDescription',
          'Manage security policies',
        ),
        buttonLabel: t('governance.overview.navigationCards.viewPolicies', 'View policies'),
      },
      {
        to: '/governance/evidence',
        icon: <BookOpen className="h-5 w-5 shrink-0 text-green-600" aria-hidden />,
        title: t('governance.overview.navigationCards.evidenceLibrary', 'Evidence Library'),
        description: t(
          'governance.overview.navigationCards.evidenceLibraryDescription',
          'Store compliance evidence',
        ),
        buttonLabel: t('governance.overview.navigationCards.viewEvidence', 'View evidence'),
      },
      {
        to: '/governance/audits',
        icon: <Search className="h-5 w-5 shrink-0 text-orange-600" aria-hidden />,
        title: t('governance.overview.navigationCards.auditCenter', 'Internal Audit Center'),
        description: t(
          'governance.overview.navigationCards.auditCenterDescription',
          'Conduct and track audits',
        ),
        buttonLabel: t('governance.overview.navigationCards.viewAudits', 'View audits'),
      },
      {
        to: '/governance/risks',
        icon: <Shield className="h-5 w-5 shrink-0 text-red-600" aria-hidden />,
        title: t('governance.overview.navigationCards.riskRegister', 'Risk Register'),
        description: t(
          'governance.overview.navigationCards.riskRegisterDescription',
          'Manage security risks',
        ),
        buttonLabel: t('governance.overview.navigationCards.viewRisks', 'View risks'),
      },
    ],
    [t],
  )

  useEffect(() => {
    loadOverview()
  }, [])

  const loadOverview = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_OVERVIEW, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const result = await res.json().catch(() => ({}))
      const safeData = {
        governanceScore: result.governanceScore || { overallScore: 0, status: 'Yellow' as const },
        metrics: result.metrics || {
          activePolicies: 0,
          policiesDueForReview: { next30Days: 0, next60Days: 0, next90Days: 0 },
          openAuditFindings: { total: 0, bySeverity: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
          openRisks: { total: 0, byLevel: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
          trainingCompletionRate: '0'
        },
        charts: result.charts || {
          policyUpdateFrequency: [],
          riskHeatmap: [],
          evidenceAging: [],
          openCorrectiveActions: 0
        }
      }
      if (!res.ok) {
        setData(safeData)
        setError(t('governance.overview.error.loadFailed', 'Failed to load overview data'))
        return
      }
      setData(result.success ? result : safeData)
    } catch (err: any) {
      setData({
        governanceScore: { overallScore: 0, status: 'Yellow' as const },
        metrics: {
          activePolicies: 0,
          policiesDueForReview: { next30Days: 0, next60Days: 0, next90Days: 0 },
          openAuditFindings: { total: 0, bySeverity: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
          openRisks: { total: 0, byLevel: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
          trainingCompletionRate: '0'
        },
        charts: { policyUpdateFrequency: [], riskHeatmap: [], evidenceAging: [], openCorrectiveActions: 0 }
      })
      setError(err.message || t('governance.overview.error.loadFailed', 'Failed to load overview data'))
      console.error('[GovernanceOverview] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <PageLayout pageScope="governance-overview-page" title={t('governance.overview.title', 'Security Governance & Documentation')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  if (error && !data) {
    return (
      <PageLayout pageScope="governance-overview-page" title={t('governance.overview.title', 'Security Governance & Documentation')}>
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      </PageLayout>
    )
  }

  // Safe defaults for data
  const safeData = data || {
    governanceScore: { overallScore: 0, status: 'Yellow' as const },
    metrics: {
      activePolicies: 0,
      policiesDueForReview: { next30Days: 0, next60Days: 0 },
      openAuditFindings: { total: 0, bySeverity: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
      openRisks: { total: 0, byLevel: { Critical: 0, High: 0, Medium: 0, Low: 0 } },
      trainingCompletionRate: 0
    },
    charts: {
      policyUpdateFrequency: [],
      riskHeatmap: [],
      evidenceAging: [],
      openCorrectiveActions: 0
    }
  };
  
  const { governanceScore = { overallScore: 0, status: 'Yellow' as const }, metrics = safeData.metrics, charts = safeData.charts } = safeData;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900/20'
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/20'
    return 'bg-red-100 dark:bg-red-900/20'
  }

  const heatmapCellColor = (level: number) => {
    if (level >= 20) return 'var(--ds-heatmap-critical)'
    if (level >= 15) return 'var(--ds-heatmap-high)'
    if (level >= 10) return 'var(--ds-heatmap-medium)'
    if (level >= 5) return 'var(--ds-heatmap-low)'
    return 'var(--ds-heatmap-minimal)'
  }

  return (
    <PageLayout pageScope="governance-overview-page" title={t('governance.overview.title', 'Security Governance & Documentation')}
      actions={
        governanceScore && governanceScore.overallScore != null ? (
          <Link to="/governance/score-analytics" className="no-underline">
            <div
              className={`ds-card ${overviewStyles.governanceScoreCard} ${getScoreBgColor(governanceScore.overallScore ?? 0)}`}
            >
              <div className={overviewStyles.governanceScoreRow}>
                <span className={overviewStyles.governanceScoreLabel}>
                  {t('governance.overview.governanceScore', 'Governance Score')}
                </span>
                <span className={`${overviewStyles.governanceScoreValue} ${getScoreColor(governanceScore.overallScore ?? 0)}`}>
                  {governanceScore.overallScore ?? 0}
                </span>
                <span
                  className={`${overviewStyles.governanceScoreDot} ${
                    (governanceScore.status ?? 'Unknown') === 'Green'
                      ? 'bg-green-500'
                      : (governanceScore.status ?? 'Unknown') === 'Yellow'
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                  aria-hidden
                />
              </div>
            </div>
          </Link>
        ) : undefined
      }
    >
      {/* Navigation */}
      <section className="mb-6">
        <div className={overviewStyles.cardGrid}>
          {governanceNavigationCards.map((card) => (
            <GovernanceCard
              key={card.to}
              to={card.to}
              icon={card.icon}
              title={card.title}
              description={card.description}
              buttonLabel={card.buttonLabel}
            />
          ))}
        </div>
      </section>

      {/* Metrics */}
      <section className="mb-6">
        <div className={overviewStyles.cardGrid}>
          <div className={`ds-card ${overviewStyles.metricCard}`}>
            <div className={overviewStyles.metricCardHeader}>
              <FileText className="h-5 w-5 shrink-0 text-blue-600" aria-hidden />
              <h2 className={overviewStyles.metricCardTitle}>
                {t('governance.overview.activePolicies', 'Active Policies')}
              </h2>
            </div>
            <div className={overviewStyles.metricCardBody}>
              <p className={`${overviewStyles.metricTotal} ${overviewStyles.metricTotalBlue}`}>
                {metrics?.activePolicies ?? 0}
              </p>
              <div className={overviewStyles.metricMeta}>
                {(metrics?.policiesDueForReview?.next30Days ?? 0) > 0 && (
                  <Link to="/governance/policies" className="font-medium text-orange-600 hover:text-orange-800 hover:underline">
                    {t('governance.overview.metrics.dueIn30Days', '{count} due in 30 days', { count: metrics?.policiesDueForReview?.next30Days ?? 0 })}
                  </Link>
                )}
                {(metrics?.policiesDueForReview?.next30Days ?? 0) === 0 && (metrics?.policiesDueForReview?.next60Days ?? 0) > 0 && (
                  <Link to="/governance/policies" className="text-yellow-600 hover:text-yellow-800 hover:underline">
                    {t('governance.overview.metrics.dueIn60Days', '{count} due in 60 days', { count: metrics?.policiesDueForReview?.next60Days ?? 0 })}
                  </Link>
                )}
                {(metrics?.policiesDueForReview?.next30Days ?? 0) === 0 && (metrics?.policiesDueForReview?.next60Days ?? 0) === 0 && (
                  <span className="text-green-600">{t('governance.overview.metrics.allUpToDate', 'All up to date')}</span>
                )}
              </div>
              <Button
                type="button"
                size="small"
                color="primary"
                fullWidth
                className={overviewStyles.metricButton}
                icon={<ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                iconPosition="end"
                onClick={() => navigate('/governance/policies')}
              >
                {t('governance.overview.metrics.viewAll', 'View all')}
              </Button>
            </div>
          </div>

          <div className={`ds-card ${overviewStyles.metricCard}`}>
            <div className={overviewStyles.metricCardHeader}>
              <AlertTriangle className="h-5 w-5 shrink-0 text-orange-600" aria-hidden />
              <h2 className={overviewStyles.metricCardTitle}>
                {t('governance.overview.openFindings', 'Open Audit Findings')}
              </h2>
            </div>
            <div className={overviewStyles.metricCardBody}>
              <div className={overviewStyles.problemGrid}>
                {GOVERNANCE_SEVERITY_KEYS.map((sev) => (
                  <ProblemCard
                    key={`audit-${sev}`}
                    title={t(`governance.overview.metrics.${sev.toLowerCase()}`, sev)}
                    value={
                      <Link
                        to="/governance/audits"
                        className={overviewStyles.problemLink}
                      >
                        {metrics?.openAuditFindings?.bySeverity?.[sev] ?? 0}
                      </Link>
                    }
                    tone={SEVERITY_TO_TONE[sev]}
                    className={overviewStyles.problemTile}
                  />
                ))}
              </div>
              <Button
                type="button"
                size="small"
                color="primary"
                fullWidth
                className={overviewStyles.metricButton}
                icon={<ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                iconPosition="end"
                onClick={() => navigate('/governance/audits')}
              >
                {t('governance.overview.metrics.viewAll', 'View all')}
              </Button>
            </div>
          </div>

          <div className={`ds-card ${overviewStyles.metricCard}`}>
            <div className={overviewStyles.metricCardHeader}>
              <Shield className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
              <h2 className={overviewStyles.metricCardTitle}>
                {t('governance.overview.openRisks', 'Open Risks')}
              </h2>
            </div>
            <div className={overviewStyles.metricCardBody}>
              <div className={overviewStyles.problemGrid}>
                {GOVERNANCE_SEVERITY_KEYS.map((sev) => (
                  <ProblemCard
                    key={`risk-${sev}`}
                    title={t(`governance.overview.metrics.${sev.toLowerCase()}`, sev)}
                    value={
                      <Link
                        to="/governance/risks"
                        className={overviewStyles.problemLink}
                      >
                        {metrics?.openRisks?.byLevel?.[sev] ?? 0}
                      </Link>
                    }
                    tone={SEVERITY_TO_TONE[sev]}
                    className={overviewStyles.problemTile}
                  />
                ))}
              </div>
              <Button
                type="button"
                size="small"
                color="primary"
                fullWidth
                className={overviewStyles.metricButton}
                icon={<ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                iconPosition="end"
                onClick={() => navigate('/governance/risks')}
              >
                {t('governance.overview.metrics.viewAll', 'View all')}
              </Button>
            </div>
          </div>

          <div className={`ds-card ${overviewStyles.metricCard}`}>
            <div className={overviewStyles.metricCardHeader}>
              <Users className="h-5 w-5 shrink-0 text-green-600" aria-hidden />
              <h2 className={overviewStyles.metricCardTitle}>
                {t('governance.overview.trainingCompletion', 'Training Completion Rate')}
              </h2>
            </div>
            <div className={overviewStyles.metricCardBody}>
              <p className={`${overviewStyles.metricTotal} ${overviewStyles.metricTotalGreen}`}>
                {metrics?.trainingCompletionRate ?? 0}%
              </p>
              <p className={overviewStyles.metricMeta}>{t('governance.overview.percentage', 'Percentage')}</p>
              <Button
                type="button"
                size="small"
                color="primary"
                fullWidth
                className={overviewStyles.metricButton}
                icon={<ArrowRight className="h-3.5 w-3.5" aria-hidden />}
                iconPosition="end"
                onClick={() => navigate('/governance/training')}
              >
                {t('governance.overview.metrics.viewAll', 'View all')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics & Reports */}
      <section className="mb-6">
        <div className="ds-card flex w-full flex-col p-4">
            <h2 className="mb-4 text-base font-semibold text-[var(--ds-text)]">{t('governance.overview.analytics.title', 'Analytics & Reports')}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1">
              {/* Policy Update Frequency */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-semibold">{t('governance.overview.analytics.policyUpdateFrequency', 'Policy Update Frequency')}</h3>
                </div>
                <div className="flex-1 min-h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts?.policyUpdateFrequency ?? []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="var(--ds-chart-line)" strokeWidth={2} name={t('governance.overview.analytics.policiesUpdated', 'Policies Updated')} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Open Corrective Actions */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                  <h3 className="text-base font-semibold">{t('governance.overview.analytics.openCorrectiveActions', 'Open Corrective Actions')}</h3>
                </div>
                <div className="flex-1 flex items-center justify-center min-h-[300px]">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-orange-600">{charts?.openCorrectiveActions ?? 0}</p>
                    <p className="text-sm text-gray-500 mt-2">{t('governance.overview.analytics.findingsRequiringAction', 'Findings requiring action')}</p>
                  </div>
                </div>
              </div>

              {/* Risk Heatmap */}
              <div className="flex flex-col">
                <h3 className="text-base font-semibold mb-4">{t('governance.overview.analytics.riskHeatmap', 'Risk Heatmap')}</h3>
                <div className="flex-1 space-y-2">
                  <div className="text-xs text-gray-500 mb-2 text-right">{t('governance.overview.analytics.impact', 'Impact')} →</div>
                  <div className="space-y-1">
                    {[5, 4, 3, 2, 1].map((impact, i) => (
                      <div key={impact} className="flex items-center gap-2">
                        <div className="w-12 text-xs text-gray-600 text-right">{impact}</div>
                        <div className="flex gap-1 flex-1">
                          {[1, 2, 3, 4, 5].map((likelihood, j) => {
                            const cell = charts?.riskHeatmap?.[i]?.[j] || { count: 0, risks: [] }
                            const level = impact * likelihood
                            return (
                              <div
                                key={`${impact}-${likelihood}`}
                                className="flex-1 aspect-square border border-gray-300 rounded flex items-center justify-center text-white text-xs font-bold cursor-pointer hover:opacity-80"
                                style={{ backgroundColor: heatmapCellColor(level) }}
                                title={`Impact: ${impact}, Likelihood: ${likelihood}, Count: ${cell.count}`}
                              >
                                {cell.count > 0 ? cell.count : ''}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-12"></div>
                    <div className="flex gap-1 flex-1">
                      {[1, 2, 3, 4, 5].map(l => (
                        <div key={l} className="flex-1 text-center text-xs text-gray-600">{l}</div>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 text-center">← {t('governance.overview.analytics.likelihood', 'Likelihood')}</div>
                </div>
              </div>

              {/* Evidence Aging */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-semibold">{t('governance.overview.analytics.evidenceAging', 'Evidence Aging')}</h3>
                </div>
                <div className="flex-1 min-h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts?.evidenceAging ?? []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="days" label={{ value: t('governance.overview.analytics.daysOld', 'Days Old'), position: 'insideBottom', offset: -5 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="var(--ds-chart-bar)" name={t('governance.overview.analytics.evidenceItems', 'Evidence Items')} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
        </div>
      </section>
    </PageLayout>
  )
}

export default GovernanceOverview
